/** Parse PieSocket publish envelope { event, data }. */
export function parsePieSocketEvent(raw) {
	if (!raw || typeof raw !== "object") return { event: null, data: null };
	if (raw.event) {
		return { event: raw.event, data: raw.data && typeof raw.data === "object" ? raw.data : {} };
	}
	if (raw.message && typeof raw.message === "object" && raw.message.event) {
		return parsePieSocketEvent(raw.message);
	}
	if (raw.type) {
		return { event: raw.type, data: raw.payload && typeof raw.payload === "object" ? raw.payload : raw };
	}
	return { event: null, data: raw };
}

/** Normalize WS frames from sbend (flat), pieSocket POC (nested payload), or PieSocket { event, data }. */
export function normalizeWsMessage(raw) {
	if (!raw || typeof raw !== "object") return { type: null };
	if (raw.event) {
		const { event, data } = parsePieSocketEvent(raw);
		if (event === "chat_message") {
			const norm = normalizePieSocketMessage(data);
			return {
				type: "chat.message",
				...data,
				message: norm,
				senderId: data.senderId,
				senderType: norm?.senderType,
			};
		}
		return { type: event, ...data, data };
	}
	const type = raw.type;
	const p = raw.payload && typeof raw.payload === "object" ? raw.payload : raw;
	const message = p.message && typeof p.message === "object" ? p.message : p;
	return { type, ...p, message: message.message ?? message.text ?? p.message };
}

export function chatIdOf(item) {
	if (!item) return null;
	return item.chatId ?? item.chat_id ?? item.sessionId ?? item.uuid ?? item.id ?? null;
}

/** @deprecated use chatIdOf */
export function sessionUuid(session) {
	return chatIdOf(session);
}

export function roomIdOf(item) {
	if (!item) return null;
	return item.roomId ?? item.room_id ?? null;
}

export function normalizePieSocketMessage(data) {
	if (!data) return null;
	const from = data.from || data.senderType || "system";
	let senderType = "system";
	if (from === "agent" || from === "executive") senderType = "executive";
	else if (from === "user" || from === "customer") senderType = "customer";
	else if (from === "ai") senderType = "ai";

	const createdAt = data.timestamp || data.createdAt || data.created_at || null;

	return {
		id:
			data.messageId ||
			data.id ||
			`${data.senderId || senderType}-${createdAt || Date.now()}`,
		senderId: data.senderId != null ? String(data.senderId) : undefined,
		senderType,
		message: data.text || data.message || "",
		createdAt,
		tempId: data.tempId,
	};
}

/** Merge a WS or REST message into the thread, replacing optimistic agent sends when echoed. */
export function upsertChatMessage(list, incoming, { userId } = {}) {
	const norm = normalizePieSocketMessage(incoming);
	if (!norm) return list;

	const idx = list.findIndex(
		(m) => m.id === norm.id || (norm.tempId && m.tempId === norm.tempId)
	);
	if (idx >= 0) {
		const next = [...list];
		next[idx] = { ...next[idx], ...norm };
		return next;
	}

	const isOwnAgentEcho =
		norm.senderType === "executive" &&
		userId &&
		incoming.senderId != null &&
		String(incoming.senderId) === String(userId);

	if (isOwnAgentEcho) {
		const optIdx = list.findLastIndex(
			(m) => m.tempId && m.senderType === "executive" && m.message === norm.message
		);
		if (optIdx >= 0) {
			const next = [...list];
			next[optIdx] = { ...norm, tempId: list[optIdx].tempId };
			return next;
		}
	}

	const dupeIdx = list.findIndex(
		(m) =>
			m.message === norm.message &&
			m.senderType === norm.senderType &&
			m.createdAt === norm.createdAt &&
			String(m.senderId || "") === String(norm.senderId || "")
	);
	if (dupeIdx >= 0) return list;

	return [...list, norm];
}

export function inboxItemToSession(data, statusOverride) {
	if (!data) return null;
	const st =
		statusOverride ||
		(data.status ? String(data.status).toLowerCase() : data.agentId ? "active" : "waiting");
	const createdAt =
		data.createdAt ||
		data.joinedAt ||
		data.dateTime ||
		data.timestamp ||
		new Date().toISOString();
	return {
		chatId: data.chatId,
		roomId: data.roomId,
		websiteId: data.websiteId,
		visitorId: data.visitorId,
		pageUrl: data.url || data.pageUrl || data.page_url,
		status: st,
		agentId: data.agentId,
		agentName: data.agentName,
		customerName: data.customerName || data.customer_name || null,
		customerEmail: data.customerEmail || data.customer_email || null,
		agentSessionId: data.agentSessionId || data.agent_session_id || null,
		presenceStatus: data.presenceStatus || data.presence_status || null,
		createdAt,
		dateTime: data.dateTime || createdAt,
	};
}

export function extractMessage(payload, fallback) {
	if (typeof payload === "string" && payload.trim()) return payload;
	if (payload && typeof payload.message === "string" && payload.message.trim()) {
		return payload.message;
	}
	if (payload?.message && typeof payload.message.message === "string") {
		return payload.message.message;
	}
	if (payload && typeof payload.detail === "string" && payload.detail.trim()) {
		return payload.detail;
	}
	return fallback;
}

export function parseLiveChatResponse(res, fallbackMessage = "Request failed") {
	if (res === "Invalid" || res === undefined || res === null) {
		return { ok: false, message: fallbackMessage, data: null };
	}
	if (res.status === "auth_error") {
		return { ok: false, message: extractMessage(res, fallbackMessage), data: null };
	}
	if (
		res.status === false ||
		res.status === "failure" ||
		res.status === "api_error" ||
		res.status === "server_error" ||
		res.status === "application_error" ||
		res.status === "not_found_error"
	) {
		return {
			ok: false,
			message: extractMessage(res.message ?? res, fallbackMessage),
			data: res.data ?? null,
		};
	}
	if (res.status !== "success" && res.status !== true) {
		return {
			ok: false,
			message: extractMessage(res.message ?? res, fallbackMessage),
			data: null,
		};
	}
	return {
		ok: true,
		message: extractMessage(res, null),
		data: res.data ?? res,
	};
}

/** Merge AI turns and live messages into a single chronological thread. */
export function mergeConversationThread(turns, liveMessages) {
	const items = [];
	(turns || []).forEach((turn, idx) => {
		const ts = turn.timestamp ? new Date(turn.timestamp).getTime() : idx * 2;
		if (turn.question) {
			items.push({
				kind: "ai_turn_question",
				text: turn.question,
				timestamp: turn.timestamp,
				sortKey: ts,
				turnIdx: idx,
			});
		}
		if (turn.answer) {
			items.push({
				kind: "ai_turn_answer",
				text: turn.answer,
				products: turn.products,
				timestamp: turn.timestamp,
				sortKey: ts + 1,
				turnIdx: idx,
			});
		}
	});
	(liveMessages || []).forEach((msg, idx) => {
		const senderType = msg.senderType || msg.sender_type || "system";
		const text = msg.message || msg.text || msg.content || "";
		const ts = msg.createdAt || msg.created_at || msg.timestamp;
		items.push({
			kind: senderType === "system" ? "live_system" : "live_message",
			senderType,
			text,
			timestamp: ts,
			sortKey: ts ? new Date(ts).getTime() : 1000000 + idx,
		});
	});
	items.sort((a, b) => a.sortKey - b.sortKey);
	return items;
}
